import React, { KeyboardEvent, useEffect, useState } from 'react';

import useDebounce from '../../hooks/useDebounce';
import AutocompleteTextField, { AutocompleteTextFieldProps } from './AutocompleteTextField';

interface TypeaheadTextFieldProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<AutocompleteTextFieldProps<T, Multiple, DisableClearable, FreeSolo>, 'options'> {
  getOptions: (term: string) => Promise<{ label: string, value: any}[]>;
}

function TypeaheadTextField<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(props: TypeaheadTextFieldProps<T, Multiple, DisableClearable, FreeSolo>) {
  const { label, defaultValue, name, getOptions, ...rest } = props;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    e.stopPropagation();
  };

  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState(defaultValue?.toString() || '');
  const [options, setOptions] = useState([]);
  const debouncedSearchTerm = useDebounce(searchValue, 125);

  // Effect for API call
  useEffect(() => {
    const handleGenerateSearchPathAsync = async () => {
      try {
        const rawOptions = await getOptions(debouncedSearchTerm);
        if (rawOptions) {
          setIsSearching(false);
          setOptions(rawOptions);
        }
      } catch {
        setIsSearching(false);
      }
    };

    if (debouncedSearchTerm) {
      handleGenerateSearchPathAsync();
    } else {
      setOptions([]);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, getOptions]);

  return (
    <AutocompleteTextField
      name={name}
      label={label}
      size="small"
      freeSolo
      onKeyDown={onKeyDown}
      id={name}
      options={options || [{ name: 'No results found' }]}
      onInputChange={(event, value) => {
        setSearchValue(value.toString());
      }}
      loading={isSearching}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
}

export default TypeaheadTextField;